import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    PENDING, IDLE, ACTIVE, INACTIVE, VIEW, EDIT, DATE_FORMAT, PAYMENT_FREE,
} from "const"
import { mtAccountsRoute, mtAccountRoute } from "client/parts/routes"
import { MtAccount, MtAccountCreate, MtAccountPackageCreate } from "server/models"
import { updateServerUserData } from "client/socketApi"
import {
    useInput,
    timezones,
    Timezone,
    StateType,
    replaceHistoryState,
    forceRerenderContent,
} from "client/utils"
import {
    StoreType,
    UserData,
    AccountValidation,

    SaveAccount,
    SaveAccountPackage,

    setAccountEdit,
    setAccountReloadList,
    setAccountReloadView,
    setAccountReloadEdit,
    setSystemUserData,
    closeModal,
} from "client/store"
import {
    PageForm,
    FormGroup,
    Label,
    Input,
    Feedback,
    DatePicker,
    SelectDropdown,
    SelectDropdownItem,
    FileLoader,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditAccountProps {
    account: MtAccount
}

let isMounted: boolean

const Form = ({
    account,
}: EditAccountProps) => {
    const userData = useSelector((state: StoreType) => state.system.userData)
    const modals = useSelector((state: StoreType) => state.system.modals)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(account.name)
    const [shortName, setShortName] = useInput(account.shortName)
    const [url, setUrl] = useInput(account.url)

    const [demoExpiry, setDemoExpiry] = useState(account.demoExpiry)
    const [timezone, setTimezone] = useState(account.timezone)
    const [status, setStatus] = useState(account.status)
    const [logoId, setLogoId] = useState(account.logoId)
    const [logo, setLogo] = useState(account.logo)

    const [validation, setValidation] = useState<AccountValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    const onClose = () => {
        if (!isMounted) return

        if (modals.length) {
            dispatch(closeModal())
            return
        }

        let path
        if (userData.accountId === 1) {
            path = account.id
                ? `${mtAccountsRoute}/${VIEW}/${account.id}`
                : mtAccountsRoute
        } else {
            path = `${mtAccountRoute}/${VIEW}`
        }

        forceRerenderContent(path, dispatch)
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const newAccountData: MtAccountCreate = {
            name,
            shortName,
            logoId,
            url,
            demoExpiry,
            timezone,
            status,
        }

        SaveAccount(account.id, newAccountData, lang)
        .then((res) => {
            if (isMounted) {
                setValidation(res.validation)
                setSaveState(IDLE)
                setApplyState(IDLE)

                if (res.newAccountId) replaceHistoryState(`${mtAccountsRoute}/${EDIT}/${res.newAccountId}`)
            }
            if (res.validation) return

            const promiseArray: Promise<any>[] = []
            if (res.newAccountId) {
                const start = new Date()
                const end = new Date()
                end.setDate(start.getDate() + 30)

                const demoAccountPackage: MtAccountPackageCreate = {
                    accountId: res.newAccountId,
                    packageId: 1,
                    dateStart: start,
                    dateEnd: end,
                    paymentOption: PAYMENT_FREE,
                    stripePaymentId: null,
                    usedSilCalculations: 0,
                    additionalSilCalculations: 0,
                }
                promiseArray.push(
                    SaveAccountPackage(demoAccountPackage, lang),
                )
            }

            Promise.all(promiseArray).then(() => {
                dispatch(setAccountEdit({
                    id: res.newAccountId || account.id,
                    logo,
                    ...newAccountData,
                }))

                if (userData.accountId === account.id) { // if user is editing own account
                    const newSystemUserData: UserData = {
                        ...userData,
                        accountName: name,
                        accountLogo: logo,
                    }
                    dispatch(setSystemUserData(newSystemUserData))
                    updateServerUserData(newSystemUserData)
                }

                dispatch(setAccountReloadList(false))
                dispatch(setAccountReloadView(false))
                setTimeout(() => {
                    dispatch(setAccountReloadList(true))
                    dispatch(setAccountReloadView(true))
                }, 0)

                if (mode === 1) onClose()
                if (mode === 2) {
                    dispatch(setAccountReloadEdit(false))
                    setTimeout(() => {
                        dispatch(setAccountReloadEdit(true))
                    }, 0)
                }
            })
        })
    }

    return (
        <PageForm>
            <Row>
                <Col xs={12} md={8}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>
                </Col>
                <Col xs={12} md={4}>
                    <FormGroup>
                        <Label>{lang.shortName}</Label>
                        <Input type="text" value={shortName} onChange={setShortName} />
                        <Feedback>{validation && validation.shortName}</Feedback>
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col xs={12}>
                    <FormGroup>
                        <Label>{lang.url}</Label>
                        <Input type="text" value={url} onChange={setUrl} />
                        <Feedback>{validation && validation.url}</Feedback>
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.image}</Label>
                        <FileLoader
                            value={logo}
                            callback={(file) => {
                                setLogoId(file.id)
                                setLogo(file)
                            }}
                        />
                    </FormGroup>
                    {
                        userData.accountId === 1
                        && (
                            <FormGroup>
                                <Label>{lang.demoExpired}</Label>
                                <DatePicker
                                    selected={demoExpiry ? new Date(demoExpiry) : null}
                                    onChange={(date: Date) => setDemoExpiry(date)}
                                    dateFormat={DATE_FORMAT}
                                    isClearable
                                />
                                <Feedback>{validation && validation.demoExpiry}</Feedback>
                            </FormGroup>
                        )
                    }
                    <FormGroup>
                        <Label>{lang.timezone}</Label>
                        <SelectDropdown title={timezones[timezone] || timezone || ""}>
                            {
                                Object.keys(timezones).map((zone: Timezone) => (
                                    <SelectDropdownItem
                                        key={zone}
                                        onClick={() => setTimezone(zone)}
                                    >
                                        {timezones[zone]}
                                    </SelectDropdownItem>
                                ))
                            }
                        </SelectDropdown>
                        <Feedback>{validation && validation.timezone}</Feedback>
                    </FormGroup>
                    {
                        userData.accountId === 1
                        && (
                            <FormGroup>
                                <Label>{lang.status}</Label>
                                <SelectDropdown title={lang[status] || ""}>
                                    <SelectDropdownItem onClick={() => setStatus(ACTIVE)}>{lang[ACTIVE]}</SelectDropdownItem>
                                    <SelectDropdownItem onClick={() => setStatus(INACTIVE)}>{lang[INACTIVE]}</SelectDropdownItem>
                                </SelectDropdown>
                                <Feedback>{validation && validation.status}</Feedback>
                            </FormGroup>
                        )
                    }
                </Col>
                <Col xs={12}>
                    <Button variant="success" row onClick={() => onSave(1)}>
                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                    </Button>
                    <Button variant="success" row onClick={() => onSave(2)}>
                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                    </Button>
                    <Button row onClick={onClose}>{lang.cancel}</Button>
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
